/**
 * 🔍 [Phase 56.5 — 2026-07-09] Dialog "Diagnostic Sans Job"
 * extrait depuis CalendrierProduction.jsx (bloc legacy ~L2306-2403).
 *
 * Rôle : dans le panneau Sans job, l'utilisatrice peut cliquer sur 🔍 pour
 * comprendre pourquoi un employé est marqué "sans job" pour une date donnée.
 * Affiche fiche RH + verdict + BTs analysés + JSON brut pour debug avancé.
 *
 * Contrat :
 *   - `state` : { open, loading, data, employee_nom, date }
 *   - `onClose()` : ferme la modal (met open à false via setter du parent)
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export default function DiagnosticSansJobDialog({ state, onClose }) {
  const s = state || {};
  return (
    <Dialog open={!!s.open} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            🔍 Diagnostic « Sans Job » — {s.employee_nom}
          </DialogTitle>
          <DialogDescription>
            Date analysée : <strong>{s.date}</strong>
          </DialogDescription>
        </DialogHeader>
        {s.loading && (
          <p className="text-sm text-slate-500 py-4">Analyse en cours…</p>
        )}
        {!s.loading && s.data?.error && (
          <p className="text-sm text-red-600 py-4">Erreur : {s.data.error}</p>
        )}
        {!s.loading && s.data && !s.data.error && (
          <div className="space-y-4 text-sm">
            {/* Résumé fiche RH */}
            <div className="bg-slate-50 rounded-lg p-3 border">
              <p className="font-semibold text-slate-700 mb-1">📋 Fiche RH</p>
              <ul className="space-y-0.5 text-xs text-slate-600">
                <li>• Type employé : <code>{s.data.type_employe || s.data.role_terrain || '—'}</code></li>
                <li>• Actif : {s.data.est_actif === false ? '❌ NON' : '✅ Oui'}</li>
              </ul>
            </div>

            {/* Verdict */}
            <div className={`rounded-lg p-3 border-2 ${s.data.verdict?.would_appear_in_no_job ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'}`}>
              <p className="font-bold text-base">
                {s.data.verdict?.would_appear_in_no_job
                  ? '⚠️ Apparait dans SANS JOB'
                  : '✅ N\'apparait PAS dans SANS JOB'}
              </p>
              <ul className="text-xs mt-2 space-y-0.5">
                <li>• Trouvé actif sur au moins un BT : <strong>{s.data.verdict?.found_active_on_a_bt ? '✅ Oui' : '❌ Non'}</strong></li>
                <li>• Trouvé exclu d'au moins un BT : <strong>{s.data.verdict?.found_excluded_on_a_bt ? '⚠️ Oui' : 'Non'}</strong></li>
                <li>• Nombre de BT analysés ce jour : <strong>{s.data.total_bts_found}</strong></li>
              </ul>
            </div>

            {/* BTs analysés */}
            <div>
              <p className="font-semibold text-slate-700 mb-2">
                📁 BTs présents le {s.date} ({s.data.total_bts_found})
              </p>
              {(!s.data.bts_at_this_date || s.data.bts_at_this_date.length === 0) ? (
                <p className="text-xs text-slate-500 italic bg-slate-50 rounded p-2 border">
                  ⚠️ Aucun BT n'a ce jour dans <code>dates_chantier</code>. Si l'employé devait être sur un BT, c'est probablement parce que le BT a été créé pour une autre date et qu'on a ensuite étendu via <code>dates_assignees</code> sans ajouter au <code>dates_chantier</code>.
                </p>
              ) : (
                <ul className="space-y-2">
                  {s.data.bts_at_this_date.map((bt) => (
                    <li key={bt.bt_id} className="border rounded-lg p-2 bg-white">
                      <p className="font-semibold text-slate-800 text-xs">
                        BT {bt.bt_numero || bt.bt_id?.slice(0, 8)} {bt.est_interne && <span className="text-purple-600">[Interne]</span>}
                      </p>
                      <ul className="text-[11px] text-slate-600 mt-1 space-y-0.5">
                        <li>• Chef du BT : <code>{bt.chef_equipe_nom || bt.chef_equipe_id || '—'}</code></li>
                        {bt.is_chef_of_this_bt && (
                          <li>• Cet employé est <strong>CHEF</strong> de ce BT. Assigné aujourd'hui : {bt.chef_assigned_today ? '✅ Oui' : '❌ Non (chef_dates_assignees ne contient pas ce jour)'}</li>
                        )}
                        <li>• Présent dans <code>employes_assignes</code> ? {bt.member_record_found ? '✅ Oui' : '❌ NON'}</li>
                        {bt.assigned_via_team_default && (
                          <li className="text-green-700">• ✅ <strong>Assigné via équipe par défaut</strong> (employes_assignes vide mais fait partie de l'équipe du chef)</li>
                        )}
                        {bt.member_record_found && (
                          <li className="ml-3">↳ <code>dates_assignees</code> : <code className="break-all">{JSON.stringify(bt.member_dates_assignees || 'absent (= toutes les dates_chantier)')}</code></li>
                        )}
                        {bt.member_record_found && (
                          <li className="ml-3">↳ Assigné aujourd'hui : {bt.member_assigned_today ? '✅ Oui' : '❌ NON (dates_assignees ne contient pas ce jour) ← raison'}</li>
                        )}
                        <li>• Dans <code>employes_exclus</code> ? {bt.is_in_employes_exclus ? '⚠️ OUI ← peut être marqué "exclus"' : 'Non'}</li>
                        <li>• <code>chef_dates_assignees</code> : <code className="break-all">{JSON.stringify(bt.chef_dates_assignees || 'absent')}</code></li>
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* JSON brut pour debug avancé */}
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Voir le JSON brut</summary>
              <pre className="mt-2 bg-slate-900 text-green-300 p-2 rounded overflow-x-auto text-[10px]">
                {JSON.stringify(s.data, null, 2)}
              </pre>
            </details>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} data-testid="close-diag-btn">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
